import {FC, useRef, useEffect, useState} from 'react'
import {shallowEqual, useSelector, connect, useDispatch, ConnectedProps} from 'react-redux'
import axios from 'axios'
import {useAuth} from '@be-infinity-ltd/auth-react'
import {LayoutSplashScreen} from '../../../../_metronic/layout/core'
import RestrictedScreen from '../components/RestrictedScreen'
import * as auth from './AuthRedux'
import {RootState} from '../../../../setup'
import {SSO_ENABLED} from '../../../../setup/keycloak/authConfig'
import {BACKEND_API_V2} from '../../../../config/config'
import {decryptPermissionStatus} from '../../../../config/utils'

// Public, pre-auth routes the app serves itself — signup via a replica link, email verification, and
// the legacy forgot/reset-password pages. Under SSO these must NOT be bounced to Keycloak: a
// prospective member who opens a replica signup link has no session and has to reach the form; a
// reset/verify link lands here without one too. Keycloak still owns the in-app LOGIN and its own
// reset flow — these are only the app-hosted surfaces that exist BEFORE a session. Prefix match on a
// lower-cased pathname (mirrors Routes.tsx: `/replica/` includes(), noLayoutPaths, `/verify_email`).
const PUBLIC_AUTH_PATH_PREFIXES = [
  '/replica/',
  '/verify_email',
  '/auth/forgotpassword',
  '/auth/resetpassword',
  // Staff auth is DEFERRED — staff sign in with the legacy JWT flow (/staff/login), NOT Keycloak. The
  // ENTIRE /staff area must bypass the KC gate so staff reach the legacy login AND their authenticated
  // pages afterwards (the staff area self-guards on the legacy token). Re-scope to KC once staff migrate.
  '/staff',
]
const isPublicAuthPath = (pathname: string): boolean => {
  const p = pathname.toLowerCase()
  return PUBLIC_AUTH_PATH_PREFIXES.some((prefix) => p.startsWith(prefix))
}

// Staff auth is DEFERRED to the legacy flow: a staff session is a legacy JWT (localStorage `token`)
// plus permission==1 (localStorage `data`). AuthInit must treat this as authenticated and NOT bounce
// to Keycloak — otherwise a staff member signs in, lands on a non-/staff page (e.g. /dashboard), and
// the KC gate redirects them to the Keycloak login. Cleared on logout (token/data removed).
const isLegacyStaffSession = (): boolean => {
  try {
    if (!localStorage.getItem('token')) return false
    return decryptPermissionStatus(localStorage.getItem('data') || '') === 1
  } catch {
    return false
  }
}

const mapState = (state: RootState) => ({auth: state.auth})
const connector = connect(mapState, auth.actions)
type PropsFromRedux = ConnectedProps<typeof connector>

const AuthInit: FC<PropsFromRedux> = (props) => {
  const didRequest = useRef(false)
  const dispatch = useDispatch()
  const [showSplashScreen, setShowSplashScreen] = useState(true)
  // Set when the getUser bridge gets a 403 restricted/deactivate — renders the blocked screen instead
  // of booting the app or bouncing to Keycloak. Deterministic across reloads (the bridge re-runs and
  // re-sets it), and never loops because 403 is not treated as a dead session.
  const [blocked, setBlocked] = useState<null | 'restricted' | 'deactivate'>(null)
  const accessToken = useSelector<RootState>(({auth}) => auth.token, shallowEqual)

  // --- SSO / Keycloak token bridge ---------------------------------------
  // useAuth() is always called (hooks must be unconditional) but is only USED
  // when SSO_ENABLED. When the flag is off, react-oidc-context provides an
  // inert context (AuthProvider is not mounted) and this value is ignored.
  const oidc = useAuth()
  const [bridgeReady, setBridgeReady] = useState(!SSO_ENABLED)
  // One-shot guard so the "no session → Keycloak" hand-off fires exactly once (and can re-arm if it
  // fails). Under SSO there is NO in-app login page — every unauthenticated state redirects to KC.
  const signinFired = useRef(false)
  // One-shot guard for the UPDATE_EMAIL Application-Initiated Action. SecuritySignIn opens a NEW tab
  // at `${origin}/?kcAction=update_email`; that tab lands here, and we fire signinRedirect with
  // kc_action so oidc-client-ts builds the required PKCE + state (a hand-built authorize URL is
  // rejected because the backoffice-web KC client enforces PKCE/S256). The original tab never carries
  // the marker, so it never navigates. Only the new tab does.
  const kcActionFired = useRef(false)
  // One-shot guard for dead-session recovery (below). Per page load; re-arms only if the redirect fails.
  const deadSessionFired = useRef(false)

  // Legacy behavior — unchanged from the original AuthInit.
  useEffect(() => {
    if (SSO_ENABLED) return

    const requestUser = async () => {
      return () => (didRequest.current = true)
    }

    if (accessToken) {
      requestUser()
    } else {
      dispatch(props.logout())
      setShowSplashScreen(false)
    }
    // eslint-disable-next-line
  }, [])

  // SSO bridge — populate redux (via actions.login) AND localStorage('token')
  // so both the axios interceptor and the ~400 scattered localStorage reads
  // keep working, then render children through the existing Routes gate.
  useEffect(() => {
    if (!SSO_ENABLED || !oidc) return
    if (oidc.isLoading) return

    // A logout is "in progress" while this marker is set. The KEY fix: it is STICKY — it forces
    // prompt=login on EVERY signin redirect until a real login actually succeeds, and is cleared only
    // when isAuthenticated becomes true (below), NOT on the redirect itself. Consuming it on the first
    // redirect was the bug: the *next* navigation fired signinRedirect WITHOUT prompt=login, and
    // Keycloak's still-alive session silently re-authenticated the user → "logout doesn't stick".
    const loggingOut = sessionStorage.getItem('sso_logout') === '1'

    if (!oidc.isAuthenticated) {
      // Public pre-auth routes (signup /replica, /verify_email, forgot/reset-password) must render
      // WITHOUT a session — never bounce a prospective member to Keycloak. Skip the hand-off here and
      // let Routes.tsx render the public route (the render gate below matches this).
      if (isPublicAuthPath(window.location.pathname)) return
      // A legacy STAFF session (deferred from SSO) is authenticated via its own JWT — do not bounce it
      // to Keycloak just because there is no KC session.
      if (isLegacyStaffSession()) return
      // Not authenticated → hand off to Keycloak (KC owns login; there is no in-app /auth/login).
      // Always clear any lingering/expired stored user first. While logging out, force prompt=login on
      // EVERY redirect so a lingering KC session cannot silently re-auth.
      if (signinFired.current) return
      signinFired.current = true
      oidc
        .removeUser()
        .catch(() => {})
        .then(() => oidc.signinRedirect(loggingOut ? {prompt: 'login'} : undefined))
        .catch((e) => {
          // eslint-disable-next-line no-console
          console.error('[SSO-v2] signinRedirect failed — re-arming', e)
          signinFired.current = false
        })
      return
    }

    // Authenticated. If we were mid-logout, a real login just completed → clear the logout marker now.
    if (loggingOut) sessionStorage.removeItem('sso_logout')
    signinFired.current = false

    if (didRequest.current) return
    didRequest.current = true

    const bridge = async () => {
      const accessToken = oidc.user?.access_token
      if (!accessToken) {
        oidc.signinRedirect()
        return
      }

      localStorage.setItem('token', accessToken)

      try {
        // NOTE: getUser's route has NO `/user` prefix (serverless http path is just "getUser";
        // BACKEND_API_V2 already includes the stage). Local offline: http://localhost:4005/dev/getUser.
        const {data} = await axios.get(`${BACKEND_API_V2}/getUser`, {
          headers: {'x-access-token': accessToken},
        })

        dispatch(
          props.login(
            0,
            accessToken,
            data?.user,
            data?.id,
            data?.email,
            data?.first_name,
            data?.second_name,
            data?.birth,
            data?.streetaddress,
            data?.suite,
            data?.postalcode,
            data?.cityaddress,
            data?.country_name ?? data?.country,
            data?.timezone,
            data?.photo_url,
            data?.cryptowallet,
            data?.security_flag,
            data?.restriction_flag,
            data?.confirm_flag
          )
        )
        // Healthy authenticated bootstrap → clear the recovery loop-breaker latch so a genuine LATER
        // session death (expiry / revoked by an email change) can recover again.
        sessionStorage.removeItem('sso_recovery_attempted')
      } catch (e) {
        // A restricted/deactivated account is blocked from the Backoffice SERVER-SIDE: unifiedAuth
        // returns 403 {type:'restricted'|'deactivate'} for every bo-api call (the SSO-first app never
        // hits /user/login, so the block lives in the API middleware). That 403 surfaces HERE as the
        // getUser bridge failing. We must NOT boot the degraded shell for it (that is what let a
        // restricted user "log in" to an empty backoffice) and must NOT bounce to Keycloak (403 is an
        // authorization failure, not a dead session — a bounce would silently re-auth and loop). Instead
        // hard-block: drop the token and render the blocked screen. Any OTHER error falls through to the
        // tolerant degraded boot below.
        const status = (e as any)?.response?.status
        const type = (e as any)?.response?.data?.type
        if (status === 403 && (type === 'restricted' || type === 'deactivate')) {
          // eslint-disable-next-line no-console
          console.warn(`[SSO] account blocked from Backoffice (${type}) — denying session`)
          try {
            localStorage.removeItem('token')
          } catch {}
          setBlocked(type)
          return // skip the degraded boot; `finally` still runs (bridgeReady/bootstrapped)
        }
        // Profile fetch failed but we DO have a valid KC token in localStorage
        // and redux (below); let the app boot rather than trapping on a splash.
        // eslint-disable-next-line no-console
        console.error('SSO profile bridge failed', e)
        dispatch(
          props.login(
            0,
            accessToken,
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            ''
          )
        )
      } finally {
        // Mark the session fully bootstrapped. Dead-session recovery (below + SetupAxios) stays DORMANT
        // until this is set, so a tolerated getUser failure during initial login can't trigger a
        // re-login loop on the OIDC callback. From here on, a 401/expiry means the session really died.
        ;(window as any).__ssoBootstrapped = true
        setBridgeReady(true)
      }
    }

    bridge()
    // eslint-disable-next-line
  }, [oidc?.isLoading, oidc?.isAuthenticated, oidc?.error])

  // --- Dead-session recovery (SSO) ---------------------------------------
  // A session can die WITHOUT the app ever flipping to "logged out", leaving the tab stuck with a dead
  // token (renders the app, every API call 401s, no redirect). Three independent triggers, one recovery:
  //   1. silent-renew failure — automaticSilentRenew is on; when the KC SSO session is killed (e.g. an
  //      email change) the refresh grant returns invalid_grant. oidc-client-ts raises silentRenewError
  //      but does NOT unload the user, so isAuthenticated stays true.
  //   2. access-token expiry — if a renew never succeeds the token just expires; again no unload.
  //   3. API 401 / `auth:false` — SetupAxios dispatches `sso:session-dead` when a request comes back
  //      unauthenticated on a token we thought was good.
  // Recovery hands the tab back to Keycloak with a PLAIN signinRedirect (no prompt): if the SSO session
  // is still alive KC re-auths silently and the user never sees a login screen; if it's dead they log in.
  useEffect(() => {
    if (!SSO_ENABLED || !oidc?.events) return

    const recover = (reason: string) => {
      if (deadSessionFired.current) return
      // Never during initial login/bootstrap — only once a session was fully established (see the
      // bridge's finally). Otherwise a transient failure on the OIDC callback would loop to login.
      if (!(window as any).__ssoBootstrapped) return
      if (isPublicAuthPath(window.location.pathname)) return
      // A legacy staff session is not a dead KC session — never bounce it to Keycloak.
      if (isLegacyStaffSession()) return
      // LOOP-BREAKER: if we ALREADY bounced to login once and are still hitting a dead session, the
      // fresh token is being rejected too (e.g. the backend doesn't accept it). Do NOT bounce again —
      // that is the infinite login loop. Boot degraded instead. The flag is cleared on the next
      // successful authenticated bootstrap (bridge success), so a real later death can still recover.
      if (sessionStorage.getItem('sso_recovery_attempted') === '1') {
        // eslint-disable-next-line no-console
        console.warn(`[SSO-v2] dead session (${reason}) but recovery already attempted — not looping`)
        return
      }
      deadSessionFired.current = true
      sessionStorage.setItem('sso_recovery_attempted', '1')
      // Also block the bridge effect's own hand-off so we don't double-redirect when removeUser flips
      // isAuthenticated to false.
      signinFired.current = true
      // eslint-disable-next-line no-console
      console.warn(`[SSO-v2] dead session (${reason}) → re-authenticating`)
      localStorage.removeItem('token')
      oidc
        .removeUser()
        .catch(() => {})
        .then(() => oidc.signinRedirect())
        .catch((e) => {
          // eslint-disable-next-line no-console
          console.error('[SSO-v2] dead-session signinRedirect failed — re-arming', e)
          deadSessionFired.current = false
          signinFired.current = false
        })
    }

    const onRenewError = () => recover('silent-renew-error')
    const onExpired = () => recover('access-token-expired')
    const onApiDead = () => recover('api-unauthenticated')

    oidc.events.addSilentRenewError(onRenewError)
    oidc.events.addAccessTokenExpired(onExpired)
    window.addEventListener('sso:session-dead', onApiDead)

    return () => {
      oidc.events.removeSilentRenewError(onRenewError)
      oidc.events.removeAccessTokenExpired(onExpired)
      window.removeEventListener('sso:session-dead', onApiDead)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oidc?.events])

  // UPDATE_EMAIL AIA trigger (SSO only). Runs in the NEW tab opened by SecuritySignIn, which carries
  // `?kcAction=update_email`. When present AND the tab is authenticated (shares the KC SSO cookie),
  // fire signinRedirect with kc_action so oidc-client-ts mints the PKCE code_challenge + state and
  // navigates THIS tab to KC's hosted update-email page. Kept separate from the bridge/callback logic
  // and independent of it:
  //   • Only acts on the kcAction marker — never touches the ?code=&state= callback the app already
  //     processes on return. On the return leg the marker is already gone (stripped below), so this
  //     effect no-ops and the normal callback handling runs untouched.
  //   • Strips the marker from the URL immediately (replaceState, no navigation) so it can't re-fire
  //     on a re-render or when KC returns here with ?code=&state=.
  //   • One-shot ref guards against a double-invoke (e.g. StrictMode / rapid re-render).
  useEffect(() => {
    if (!SSO_ENABLED || !oidc) return
    if (oidc.isLoading) return
    if (kcActionFired.current) return

    const params = new URLSearchParams(window.location.search)
    if (params.get('kcAction') !== 'update_email') return

    // Wait until this tab is authenticated (it shares the KC SSO cookie, so this is immediate). If it
    // somehow isn't yet, leave the marker in place — the unauthenticated hand-off above signs us in,
    // this effect re-runs on isAuthenticated, and fires then.
    if (!oidc.isAuthenticated) return

    // Strip the marker BEFORE redirecting so neither a re-render nor the OIDC ?code=&state= return to
    // this tab can re-trigger the AIA.
    params.delete('kcAction')
    const cleaned =
      window.location.pathname +
      (params.toString() ? `?${params.toString()}` : '') +
      window.location.hash
    window.history.replaceState(null, '', cleaned)

    kcActionFired.current = true
    oidc
      .signinRedirect({extraQueryParams: {kc_action: 'UPDATE_EMAIL'}})
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error('[SSO] UPDATE_EMAIL AIA redirect failed', e)
        kcActionFired.current = false
      })
    // eslint-disable-next-line
  }, [oidc?.isLoading, oidc?.isAuthenticated])

  // Keep localStorage('token') in sync with the CURRENT Keycloak access token. The bridge above runs
  // once, but automaticSilentRenew rotates the access token (~120s lifespan on dev). Without re-syncing,
  // the axios interceptor (which reads localStorage at request time) would keep sending the first,
  // now-expired token → backends return `auth:false, "Failed to authenticate token."`. This fires on
  // every access-token change and writes the fresh one.
  useEffect(() => {
    if (!SSO_ENABLED) return
    const at = oidc?.user?.access_token
    if (at) localStorage.setItem('token', at)
    // eslint-disable-next-line
  }, [oidc?.user?.access_token])

  // A blocked account (restricted/deactivated) never reaches the app or the KC login — it gets the
  // dedicated screen with the only action that makes sense: sign out. Checked first so it wins over
  // every other render branch.
  if (blocked) {
    return (
      <RestrictedScreen
        type={blocked}
        onLogout={() => {
          try {
            localStorage.clear()
          } catch {}
          sessionStorage.setItem('sso_logout', '1')
          if (SSO_ENABLED && oidc) {
            oidc.signoutRedirect().catch(() => {
              window.location.href = '/'
            })
          } else {
            window.location.href = '/'
          }
        }}
      />
    )
  }

  if (SSO_ENABLED) {
    // Public pre-auth routes render for an UNauthenticated visitor (no splash, no KC redirect) — the
    // effect above skips the hand-off for these paths, so gating on isAuthenticated here would trap
    // the signup/reset/verify flows on a permanent splash. An authenticated user on the same path
    // still falls through to the normal bridge gate below (so their token is populated).
    if (isPublicAuthPath(window.location.pathname) && !oidc?.isAuthenticated) {
      return <>{props.children}</>
    }
    // A legacy STAFF session is authenticated on its own JWT (staff auth is deferred from SSO) — render
    // the app directly instead of waiting on a KC session / splash / redirect.
    if (isLegacyStaffSession() && !oidc?.isAuthenticated) {
      return <>{props.children}</>
    }
    // Show splash while OIDC loads, while redirecting to KC, and until the
    // profile bridge has populated redux + localStorage.
    if (!oidc || oidc.isLoading || !oidc.isAuthenticated || !bridgeReady) {
      return <LayoutSplashScreen />
    }
    return <>{props.children}</>
  }

  return showSplashScreen ? <LayoutSplashScreen /> : <>{props.children}</>
}

export default connector(AuthInit)
